import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

interface PasswordStrengthProps {
  password: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthProps> = ({ password }) => {
  const getPasswordStrength = (password: string) => {
    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*]/.test(password),
    };

    score = Object.values(checks).filter(Boolean).length;
    
    let strength = 'Very Weak';
    let color = '#C53030';
    let progress = 20;

    if (score >= 2) { strength = 'Weak'; color = '#FF6905'; progress = 40; }
    if (score >= 3) { strength = 'Fair'; color = '#FFB683'; progress = 60; }
    if (score >= 4) { strength = 'Good'; color = '#4F076F'; progress = 80; }
    if (score === 5) { strength = 'Strong'; color = '#753403'; progress = 100; }

    return { strength, color, progress, checks };
  };

  if (!password) return null;

  const { strength, color, progress, checks } = getPasswordStrength(password);

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ mr: 1 }}>
          Password strength:
        </Typography>
        <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
          {strength}
        </Typography>
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: '#E8E8E8',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 2,
          },
        }}
      />

      <Box sx={{ mt: 1 }}>
        {Object.entries(checks).map(([key, valid]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {valid ? (
              <CheckCircle sx={{ fontSize: 16, color: '#753403', mr: 1 }} />
            ) : (
              <Cancel sx={{ fontSize: 16, color: '#C53030', mr: 1 }} />
            )}
            <Typography variant="caption" sx={{ color: valid ? '#753403' : '#C53030' }}>
              {key === 'length' && 'At least 8 characters'}
              {key === 'uppercase' && 'Uppercase letter'}
              {key === 'lowercase' && 'Lowercase letter'}
              {key === 'number' && 'Number'}
              {key === 'special' && 'Special character'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
